import { Popover } from '@mui/material';
import moment from 'moment-timezone';
import { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { twMerge } from 'tailwind-merge';

import CalendarIcon from '../../../assets/svg/CalendarIcon';
import CrossIcon from '../../../assets/svg/CrossIcon';
import { DATE_TIME_FORMAT } from '../../../constants';
import {
  getFormattedDateInMomentDefaultTZ,
  getNowInDefaultTimezoneAsDate,
} from '../../../helpers';
import { Button } from '../../Shared';

import './react-date-picker.css';

const DEFAULT_TITLE = 'Today';

const isSameDate = (date, comparedDate) =>
  moment(date).isSame(comparedDate, 'day');

const getTitle = (startDate, endDate) => {
  const isStartDateToday = isSameDate(
    startDate,
    getNowInDefaultTimezoneAsDate(),
  );
  const isEndDateToday = isSameDate(endDate, getNowInDefaultTimezoneAsDate());
  const isToday = isStartDateToday && isEndDateToday;

  if (isToday) {
    return DEFAULT_TITLE;
  }

  const isSameStartEndDate = isSameDate(startDate, endDate);

  if (isSameStartEndDate) {
    return `${getFormattedDateInMomentDefaultTZ(startDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`;
  }

  return `${getFormattedDateInMomentDefaultTZ(startDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)} - ${getFormattedDateInMomentDefaultTZ(endDate).format(DATE_TIME_FORMAT.MMM_D_YYYY)}`;
};

const getButtonDoneDisabledState = (
  startDate,
  endDate,
  currStartDate,
  currEndDate,
) => {
  const hasCurrEndDate = !!currEndDate;
  const isSameStartDate = isSameDate(startDate, currStartDate);
  const isSameStartEndDate = isSameDate(startDate, endDate);

  if (!hasCurrEndDate && isSameStartDate && isSameStartEndDate) return true;

  const isSameEndDate = isSameDate(endDate, currEndDate);

  return isSameStartDate && isSameEndDate;
};

export const DateRangeFilter = ({
  startDate,
  endDate,
  onChangeDateRange,
  handleDateRange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currStartDate, setCurrStartDate] = useState(startDate);
  const [currEndDate, setCurrEndDate] = useState(endDate);

  const onChangeCurrDate = ([nextStartDate, nextEndDate]) => {
    setCurrStartDate(nextStartDate);

    if (handleDateRange) {
      handleDateRange(nextStartDate, nextEndDate, setCurrEndDate);
      return;
    }

    setCurrEndDate(nextEndDate);
  };

  const onChange = () => {
    onChangeDateRange(prev => {
      return {
        ...prev,
        startDate: currStartDate,
        endDate: currEndDate ?? currStartDate,
      };
    });
    onClosePopover();
  };

  const reset = () => {
    const nextDate = getNowInDefaultTimezoneAsDate();

    onChangeDateRange(prev => {
      return {
        ...prev,
        startDate: nextDate,
        endDate: nextDate,
      };
    });
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setCurrStartDate(startDate);
    setCurrEndDate(endDate);
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const title = getTitle(startDate, endDate);
  const isToday = title === DEFAULT_TITLE;
  const isButtonDoneDisabled = getButtonDoneDisabledState(
    startDate,
    endDate,
    currStartDate,
    currEndDate,
  );

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] pl-5 pr-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          !isToday && 'bg-brand-900 text-white',
        )}
        onClick={onOpenPopover}
      >
        <CalendarIcon
          className={twMerge(
            'w-4 h-4 text-brand-500',
            !isToday && 'text-brand-300',
          )}
        />
        {title}
        {!isToday && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              'leading-[0] !rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white !overflow-auto w-[680px]',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ReactDatePicker
          selected={currStartDate}
          startDate={currStartDate}
          endDate={currEndDate}
          onChange={onChangeCurrDate}
          monthsShown={2}
          selectsRange
          inline
          calendarClassName="range-datepicker"
        />
        <div className="px-5 py-3 border-t border-t-base-200 w-[680px]">
          <Button
            title="Done"
            onClick={onChange}
            disabled={isButtonDoneDisabled}
            className="ml-auto"
          />
        </div>
      </Popover>
    </>
  );
};
